import React, { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, Pressable, StyleSheet, Text, View } from 'react-native';
import { supabase } from '../../lib/supabase';
import { useTheme, makeThemedStyles } from '../../v10/theme';
import { GlassCard } from '../../v10/ui';
import { FloRiver } from '../FloRiver';
import { buildRiverModel, type FloRiverList, type FloRiverTask, type RiverModel, type RiverItem } from '../../lib/flo-weights';
import { FloTaskSheet } from '../../tools/NumaTool';
import { BASELINE_LOAD } from '../../lib/flo-weights';
import { Gauge, rampFor, type GaugeBand } from '../Gauge';
import type { V10Palette } from '../../v10/theme';
import type { OrderWidgetProps } from './types';

// ── Gauge scales. Each maps a reading onto the arc's 0..1 badness fraction and
// places the band edges at the SAME thresholds the FLO engine already uses, so
// the orb colour is the engine's verdict, not a second opinion.

/** CHARGE 0–10, high is good. 8.0 is flo.ts's premium/throttled line. */
function chargeGauge(t: V10Palette, charge: number): { fraction: number; bands: GaugeBand[] } {
  const RAMP = rampFor(t);
  return {
    fraction: clamp01((10 - charge) / 10),
    // charge ≥ 8 green · ≥ 6 yellow · ≥ 4 orange · below that red
    bands: [
      { upTo: 0.2, color: RAMP.green },
      { upTo: 0.4, color: RAMP.yellow },
      { upTo: 0.6, color: RAMP.orange },
      { upTo: Infinity, color: RAMP.red },
    ],
  };
}

/** LOAD is open-priority points; flo-weights bands it against BASELINE_LOAD
 *  (Light < 0.75× · Normal ≤ 1.25× · Heavy ≤ 1.75× · Overloaded above). The
 *  arc spans 0–2× baseline, so those ratios land on the band edges below. */
function loadGauge(t: V10Palette, load: number): { fraction: number; bands: GaugeBand[] } {
  const RAMP = rampFor(t);
  return {
    fraction: clamp01(load / (BASELINE_LOAD * 2)),
    bands: [
      { upTo: 0.375, color: RAMP.green },
      { upTo: 0.625, color: RAMP.yellow },
      { upTo: 0.875, color: RAMP.orange },
      { upTo: Infinity, color: RAMP.red },
    ],
  };
}

/** FLOW 0–100+, high is good. flo.ts bands at 100 / 80 / 60. */
function flowGauge(t: V10Palette, score: number): { fraction: number; bands: GaugeBand[] } {
  const RAMP = rampFor(t);
  return {
    fraction: clamp01((100 - score) / 100),
    bands: [
      { upTo: 0.2, color: RAMP.green },
      { upTo: 0.4, color: RAMP.yellow },
      { upTo: 0.6, color: RAMP.orange },
      { upTo: Infinity, color: RAMP.red },
    ],
  };
}

const clamp01 = (n: number) => (Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : 0);

/** Compact FLO river widget — real FloRiver visualization + live metrics. */
export function NumaWidget({ userId, data, onOpen, onOpenTool, onEngage }: OrderWidgetProps) {
  const { t } = useTheme();
  const styles = useStyles();
  const flo = data.flo;
  const [riverModel, setRiverModel] = useState<RiverModel>(() => ({
    items: [],
    bySector: {},
    totalLoad: 0,
    loadBand: 'Normal' as const,
    offenders: [],
  }));
  const [taskCategoryById, setTaskCategoryById] = useState<Map<string, string | null>>(new Map());
  const [riverLoading, setRiverLoading] = useState(true);
  const [riverError, setRiverError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [sheetItem, setSheetItem] = useState<RiverItem | null>(null);
  const [sheetMode, setSheetMode] = useState<'details' | 'reschedule'>('details');
  const [sheetMutating, setSheetMutating] = useState(false);
  const [sheetError, setSheetError] = useState<string | null>(null);
  // The three gauges always sit on ONE row — they size themselves to whatever
  // width the band gives them rather than wrapping on a phone.
  const [metricsW, setMetricsW] = useState(0);
  const gaugeSize = Math.max(84, Math.min(160, Math.floor(((metricsW || 320) - 16) / 3)));

  // Fetch tasks, lists, and settings to build river model
  useEffect(() => {
    if (!userId) {
      setRiverLoading(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      try {
        setRiverLoading(true);
        const [tasksRes, listsRes, settingsRes] = await Promise.all([
          supabase
            .from('tasks')
            .select(
              'id, list_id, group_id, sg1_id, sg2_id, value_selections, default_priority, scheduled, category, created_at, title, done, unlisted, tabled, updated_at'
            )
            .eq('user_id', userId),
          supabase
            .from('task_lists')
            .select('id, name, olmps_sector')
            .eq('user_id', userId),
          supabase
            .from('user_settings')
            .select('groups, value_settings, unscheduled_task_priority_bonus')
            .eq('user_id', userId)
            .maybeSingle(),
        ]);

        if (cancelled) return;

        if (tasksRes.error || listsRes.error || settingsRes.error) {
          throw new Error(tasksRes.error?.message || listsRes.error?.message || settingsRes.error?.message);
        }

        const tasks = (tasksRes.data ?? []) as any[];
        const lists = (listsRes.data ?? []) as any[];
        const settings = settingsRes.data;

        // Build FloRiverTask array
        const floTasks: FloRiverTask[] = tasks.map((tk) => ({
          id: tk.id,
          title: tk.title ?? '',
          listId: tk.list_id,
          category: tk.category,
          groupId: tk.group_id,
          sg1Id: tk.sg1_id,
          sg2Id: tk.sg2_id,
          valueSelections: tk.value_selections,
          defaultPriority: tk.default_priority,
          etc: null,
          scheduled: tk.scheduled,
          done: !!tk.done,
          unlisted: !!tk.unlisted,
          createdAt: tk.created_at,
          updatedAt: tk.updated_at,
          isProjectLinked: false,
          tabled: !!tk.tabled,
        }));

        const floLists: FloRiverList[] = lists.map((l) => ({
          id: l.id,
          name: l.name,
          sector: l.olmps_sector ?? null,
        }));

        const groups = (settings?.groups ?? []) as any[];
        const valueSettings = (settings?.value_settings ?? []) as any[];
        const unscheduledBonus = settings?.unscheduled_task_priority_bonus ?? 1;

        const model = buildRiverModel(floTasks, floLists, groups, valueSettings, unscheduledBonus);
        if (!cancelled) {
          setRiverModel(model);
          setTaskCategoryById(new Map(tasks.map((tk) => [tk.id, tk.category ?? null])));
          setRiverError(null);
        }
      } catch (e) {
        if (!cancelled) {
          setRiverError(e instanceof Error ? e.message : 'Failed to load river');
        }
      } finally {
        if (!cancelled) setRiverLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [userId, refreshTick]);

  const handlePressItem = useCallback((item: RiverItem) => {
    setSheetItem(item);
    setSheetMode('details');
    setSheetError(null);
  }, []);

  const closeItemSheet = useCallback(() => {
    setSheetItem(null);
    setSheetMode('details');
    setSheetError(null);
  }, []);

  const markItemDone = useCallback(
    async (item: RiverItem) => {
      if (!userId) return;
      setSheetMutating(true);
      setSheetError(null);
      const { error } = await supabase
        .from('tasks')
        .update({ done: true, updated_at: new Date().toISOString(), scheduled_manual: false })
        .eq('id', item.id)
        .eq('user_id', userId);
      setSheetMutating(false);
      if (error) {
        setSheetError(error.message);
        return;
      }
      // Acting on an orb is NUMA's engagement for the day (green dot).
      onEngage?.();
      closeItemSheet();
      setRefreshTick((n) => n + 1);
      // Charge/Load/Flow gauges read the shared snapshot — reload it too.
      data.refresh();
    },
    [userId, closeItemSheet, onEngage, data]
  );

  const rescheduleItem = useCallback(
    async (item: RiverItem, scheduledValue: string | null) => {
      if (!userId) return;
      setSheetMutating(true);
      setSheetError(null);
      const { error } = await supabase
        .from('tasks')
        .update({ scheduled: scheduledValue, scheduled_manual: true, updated_at: new Date().toISOString() })
        .eq('id', item.id)
        .eq('user_id', userId);
      setSheetMutating(false);
      if (error) {
        setSheetError(error.message);
        return;
      }
      onEngage?.();
      closeItemSheet();
      setRefreshTick((n) => n + 1);
      data.refresh();
    },
    [userId, closeItemSheet, onEngage, data]
  );

  const openInKyrs = useCallback(() => {
    onOpenTool?.('kyrs', sheetItem ? { initialTaskId: sheetItem.id } : undefined);
    closeItemSheet();
  }, [onOpenTool, closeItemSheet, sheetItem]);

  // LOAD: flo.load is the raw open-priority sum (hundreds), but the gauge bands
  // are ratios of BASELINE_LOAD, which is the river's normalised-weight scale.
  // Once the river has loaded, read the gauge from the river's own total so the
  // needle is on the same scale as its bands (raw flo.load saturated red).
  const riverReady = !riverLoading && !riverError;
  const loadReading = riverReady ? riverModel.totalLoad : null;

  if (!flo) {
    return (
      <Pressable onPress={onOpen} style={styles.container}>
        <Text style={styles.emptyText}>{data.floError || 'Loading FLO…'}</Text>
      </Pressable>
    );
  }

  return (
    <Pressable onPress={onOpen} style={styles.container} hitSlop={0}>
      {/* River visualization */}
      {riverLoading && riverModel.items.length === 0 ? (
        <GlassCard style={styles.riverPlaceholder}>
          <ActivityIndicator color={t.goldBright} size="small" />
          <Text style={styles.riverPlaceholderText}>Loading river…</Text>
        </GlassCard>
      ) : riverError ? (
        <GlassCard style={styles.riverPlaceholder}>
          <Text style={styles.errorText}>{riverError}</Text>
        </GlassCard>
      ) : (
        <Pressable style={styles.riverWrapper} onPress={(e) => e.stopPropagation()}>
          <FloRiver
            model={riverModel}
            view="weight"
            onPressItem={handlePressItem}
            height={170}
            showLegend={false}
          />
        </Pressable>
      )}

      {/* Metrics row: three needle gauges. Each converts its own reading into
          the shared left-to-right "badness" fraction the arc ramps along, so
          the needle position and the orb colour agree across all three even
          though a high CHARGE is good and a high LOAD is not. */}
      <View
        style={styles.metricsRow}
        onLayout={(e) => setMetricsW(Math.round(e.nativeEvent.layout.width))}
      >
        <Gauge label="CHARGE" value={flo.charge.toFixed(1)} size={gaugeSize} {...chargeGauge(t, flo.charge)} />
        <Gauge
          label="LOAD"
          value={String(Math.round(loadReading ?? flo.load))}
          size={gaugeSize}
          {...loadGauge(t, loadReading ?? flo.load)}
        />
        <Gauge
          label="FLOW"
          value={String(Math.round(flo.flow.score))}
          size={gaugeSize}
          {...flowGauge(t, flo.flow.score)}
        />
      </View>

      {/* Throttled indicator */}
      {flo.mode === 'throttled' && (
        <View style={styles.throttledChip}>
          <Text style={styles.throttledText}>Throttled</Text>
        </View>
      )}

      <FloTaskSheet
        item={sheetItem}
        category={sheetItem ? taskCategoryById.get(sheetItem.id) ?? null : null}
        mode={sheetMode}
        mutating={sheetMutating}
        errorMessage={sheetError}
        onChangeMode={setSheetMode}
        onClose={closeItemSheet}
        onMarkDone={markItemDone}
        onReschedule={rescheduleItem}
        onOpenKyrs={openInKyrs}
      />
    </Pressable>
  );
}

const useStyles = makeThemedStyles(({ t }) =>
  StyleSheet.create({
  container: {
    gap: 10,
    paddingHorizontal: 2,
  },
  riverWrapper: {
    borderRadius: 16,
    overflow: 'hidden',
  },
  riverPlaceholder: {
    height: 170,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  riverPlaceholderText: {
    fontSize: 11,
    color: t.ivorySoft,
    fontStyle: 'italic',
  },
  errorText: {
    fontSize: 11,
    color: t.lapsed,
    fontStyle: 'italic',
    textAlign: 'center',
  },
  metricsRow: {
    flexDirection: 'row',
    gap: 8,
    justifyContent: 'space-around',
    alignItems: 'flex-end',
  },
  pill: {
    flex: 1,
    backgroundColor: t.gold,
    borderRadius: 999,
    paddingVertical: 6,
    paddingHorizontal: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pillText: {
    fontSize: 10,
    fontWeight: '700',
    color: t.onGold,
    textAlign: 'center',
  },
  throttledChip: {
    alignSelf: 'flex-start',
    backgroundColor: t.behind,
    boxShadow: `0 0 12px ${t.goldGlow}`,
    borderRadius: 999,
    paddingVertical: 4,
    paddingHorizontal: 10,
  },
  throttledText: {
    fontSize: 9,
    fontWeight: '600',
    color: t.onGold,
  },
  emptyText: {
    fontSize: 13,
    color: t.ivorySoft,
    fontStyle: 'italic',
  },
}),
);
